import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCurrency } from '@/lib/utils';
import {
  productService,
  ProductPrice,
  Product as ProductFromService,
} from '@/services/productService';
import { toast } from 'sonner';
import { usePriceMetadata } from '@/hooks/usePriceMetadata';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { Loader2, PencilIcon, TrashIcon } from 'lucide-react';

import {
  formatNumberInput,
  parseNumberFromInput,
  formatDateForDisplay,
  formatNumberForDisplay,
  DateFormat,
} from '@/utils/numberFormat';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { logError } from '@/utils/errorHandler';

interface PricingModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly productId?: number;
  readonly onPricesUpdated?: () => Promise<void>;
  readonly draftPrices?: ProductPrice[];
  readonly setDraftPrices?: React.Dispatch<React.SetStateAction<ProductPrice[]>>;
}

// Use the Product type from the service
type Product = ProductFromService;

// Define NewPrice type based on the shape of defaultNewPrice
// This should match the structure used in PricingModal
interface NewPrice {
  price_type_id: number;
  currency: string;
  amount: string;
  valid_from: string;
  valid_to: string | null;
}

export function PricingModal({
  isOpen,
  onClose,
  productId,
  onPricesUpdated,
  draftPrices = [],
  setDraftPrices,
}: PricingModalProps) {
  // Use the price metadata hook
  const { priceTypes, currencies } = usePriceMetadata();

  // Use organization settings to get defaults
  const { orgSettings } = useOrgSettings();
  const numberFormat = orgSettings?.number_format ?? 'dot';
  const dateFormat = (orgSettings?.date_format ?? 'MDY') as DateFormat;

  // Store product data
  const [product, setProduct] = useState<Product | null>(null);
  // Store prices
  const [prices, setPrices] = useState<ProductPrice[]>([]);
  // Track local draft prices if not provided from parent
  const [localDraftPrices, setLocalDraftPrices] = useState<ProductPrice[]>([]);

  // Use either passed draftPrices or local state
  const actualDraftPrices = setDraftPrices ? draftPrices : localDraftPrices;
  const updateDraftPrices = setDraftPrices ?? setLocalDraftPrices;

  // Loading states
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Edit price state
  const [editingPrice, setEditingPrice] = useState<ProductPrice | null>(null);
  // Track if we're editing a draft price
  const [isEditingDraft, setIsEditingDraft] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState<'table' | 'add'>('table');

  // Get pricing modal translations
  const { getPricingModalLabels, getPricingModalNotification, getProductComponentsText } =
    useProductDetailPageTranslations();

  // Memoize default price values based on organization defaults and price types
  const defaultNewPrice = useMemo(() => {
    // Use organization defaults if available, otherwise fallback to first available options
    const defaultCurrency =
      orgSettings?.default_currency?.iso_code ?? currencies[0]?.iso_code ?? 'USD';
    const defaultPriceType = orgSettings?.default_price_type?.id ?? priceTypes[0]?.id ?? 0;

    return {
      price_type_id: defaultPriceType,
      currency: defaultCurrency,
      amount: '',
      valid_from: new Date().toISOString().slice(0, 10),
      valid_to: null,
    };
  }, [priceTypes, currencies, orgSettings?.default_currency, orgSettings?.default_price_type]);

  // Initialize state with the memoized default
  const [newPrice, setNewPrice] = useState<NewPrice>(defaultNewPrice);

  // Effect to reset form state when modal opens or switching to edit mode
  useEffect(() => {
    // Only when the modal opens or you switch into "edit" mode do we reset:
    if (!isOpen) return;
    if (editingPrice) {
      const typeId =
        priceTypes.find(pt => pt.code === editingPrice.price_type)?.id ??
        defaultNewPrice.price_type_id;
      setNewPrice({
        price_type_id: typeId,
        currency: editingPrice.currency,
        amount: editingPrice.amount.toString(),
        valid_from: editingPrice.valid_from?.slice(0, 10) ?? defaultNewPrice.valid_from,
        valid_to: editingPrice.valid_to?.slice(0, 10) ?? null,
      });
    } else {
      setNewPrice(defaultNewPrice);
    }
  }, [isOpen, editingPrice, defaultNewPrice, priceTypes]);

  // Always respond to open/close, but only fetch data when we have an ID
  useEffect(() => {
    if (!isOpen) return;

    // In edit mode only:
    if (productId) {
      // Fetch product data
      const fetchProduct = async () => {
        setIsLoading(true);
        try {
          const productData = await productService.getProduct(productId);
          setProduct(productData);
        } catch (error) {
          logError(
            'Error fetching product',
            {
              component: 'PricingModal',
              action: 'fetchProduct',
              metadata: { productId, error },
            },
            { showToast: false, userFacing: false }
          );
          toast.error(getPricingModalNotification('metadataFailed'));
        } finally {
          setIsLoading(false);
        }
      };

      // Fetch prices from API
      const fetchPrices = async () => {
        setLoading(true);
        try {
          const pricesData = await productService.getPrices(productId);
          setPrices(pricesData);
        } catch (error) {
          logError(
            'Error fetching prices',
            {
              component: 'PricingModal',
              action: 'fetchPrices',
              metadata: { productId, error },
            },
            { showToast: false, userFacing: false }
          );
          toast.error(getPricingModalNotification('metadataFailed'));
        } finally {
          setLoading(false);
        }
      };

      fetchPrices();
      fetchProduct();
    }
  }, [isOpen, productId]);

  // Get the code for a price type ID
  const getPriceTypeCode = (priceTypeId: number): string => {
    const priceType = priceTypes.find(pt => pt.id === priceTypeId);
    // Return the code from the database, or 'list' as a default if not found
    return priceType?.code || 'list';
  };

  // Add a new price
  const handleAddPrice = async () => {
    // Validate required fields
    if (!newPrice.amount) {
      toast.error(getPricingModalLabels.validation.amountRequired);
      return;
    }

    // Get organization number format for parsing
    const parsedAmount = parseNumberFromInput(newPrice.amount, numberFormat);

    if (parsedAmount <= 0) {
      toast.error(getPricingModalLabels.validation.amountInvalid);
      return;
    }

    if (!productId) {
      const priceTypeCode = getPriceTypeCode(newPrice.price_type_id);
      const priceTypeLabel =
        priceTypes.find(pt => pt.id === newPrice.price_type_id)?.label || priceTypeCode;

      // Create draft price object
      const draft: ProductPrice = {
        id: Date.now(), // temporary ID for draft
        price_type: priceTypeCode,
        price_type_display: priceTypeLabel,
        channel: null,
        currency: newPrice.currency,
        amount: parsedAmount,
        valid_from: newPrice.valid_from,
        valid_to: newPrice.valid_to,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Add to draft prices
      updateDraftPrices(prev => [...prev, draft]);
      toast.success(getPricingModalNotification('priceAdded'));

      // Reset form
      setNewPrice(defaultNewPrice);

      // Switch back to table view
      setActiveTab('table');
      return;
    }

    // Edit mode - add real price
    setLoading(true);
    try {
      // Get the price type code from the ID
      const priceTypeCode = getPriceTypeCode(newPrice.price_type_id);

      const validFromISO = new Date(newPrice.valid_from).toISOString();
      const validToISO = newPrice.valid_to ? new Date(newPrice.valid_to).toISOString() : null;

      const created = await productService.addPrice(productId, {
        price_type: priceTypeCode,
        currency: newPrice.currency,
        amount: parsedAmount,
        valid_from: validFromISO,
        valid_to: validToISO,
      });

      if (created) setPrices(prev => [...prev, created]);

      toast.success(getPricingModalNotification('priceAdded'));

      // Refresh list from server in background
      const refreshPrices = async () => {
        try {
          const pricesData = await productService.getPrices(productId);
          setPrices(pricesData);
        } catch (error) {
          logError(
            'Error refreshing prices',
            {
              component: 'PricingModal',
              action: 'refreshPrices',
              metadata: { productId, error },
            },
            { showToast: false, userFacing: false }
          );
        }
      };
      refreshPrices();

      // Reset form
      setNewPrice(defaultNewPrice);

      // Switch back to table view to show the new row
      setActiveTab('table');

      // Call the onPricesUpdated callback
      if (onPricesUpdated) {
        await onPricesUpdated();
      }
    } catch (error) {
      logError(
        'Error adding price',
        {
          component: 'PricingModal',
          action: 'handleAddPrice',
          metadata: { productId, newPrice, error },
        },
        { showToast: false, userFacing: false }
      );
      toast.error(getPricingModalNotification('addFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Update an existing price
  const handleUpdatePrice = async () => {
    if (!newPrice.amount) {
      toast.error(getPricingModalLabels.validation.amountRequired);
      return;
    }

    // Get organization number format for parsing
    const parsedAmount = parseNumberFromInput(newPrice.amount, numberFormat);

    if (parsedAmount <= 0) {
      toast.error(getPricingModalLabels.validation.amountInvalid);
      return;
    }

    // Handling draft price update
    if (isEditingDraft && editingPrice) {
      const priceTypeCode = getPriceTypeCode(newPrice.price_type_id);
      const priceTypeLabel =
        priceTypes.find(pt => pt.id === newPrice.price_type_id)?.label || priceTypeCode;

      // Create updated draft price
      const updatedDraft: ProductPrice = {
        ...editingPrice,
        price_type: priceTypeCode,
        price_type_display: priceTypeLabel,
        currency: newPrice.currency,
        amount: parsedAmount,
        valid_from: newPrice.valid_from,
        valid_to: newPrice.valid_to,
        updated_at: new Date().toISOString(),
      };

      // Update draft prices
      updateDraftPrices(prevDrafts =>
        prevDrafts.map(draft => (draft.id === editingPrice.id ? updatedDraft : draft))
      );

      toast.success(getPricingModalNotification('priceUpdated'));
      setEditingPrice(null);
      setIsEditingDraft(false);
      setActiveTab('table');
      return;
    }

    // Handling real price update
    if (!productId || !editingPrice) {
      toast.error('Cannot update price');
      return;
    }

    setLoading(true);
    try {
      // Get the price type code from the ID
      const priceTypeCode = getPriceTypeCode(newPrice.price_type_id);

      const validFromISO = new Date(newPrice.valid_from).toISOString();
      const validToISO = newPrice.valid_to ? new Date(newPrice.valid_to).toISOString() : null;

      // Build diff payload – include only changed fields
      const payload: any = {};
      if (editingPrice.price_type !== priceTypeCode) payload.price_type = priceTypeCode;
      if (editingPrice.currency !== newPrice.currency) payload.currency = newPrice.currency;
      if (editingPrice.amount !== parsedAmount) payload.amount = parsedAmount;
      if (editingPrice.valid_from?.slice(0, 10) !== newPrice.valid_from)
        payload.valid_from = validFromISO;
      if ((editingPrice.valid_to ? editingPrice.valid_to.slice(0, 10) : null) !== newPrice.valid_to)
        payload.valid_to = validToISO;

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to update');
        setLoading(false);
        return;
      }

      await productService.patchPrice(productId, editingPrice.id, payload);

      toast.success(getPricingModalNotification('priceUpdated'));

      // Refresh prices
      const refreshPricesAfterUpdate = async () => {
        try {
          const pricesData = await productService.getPrices(productId);
          setPrices(pricesData);
        } catch (error) {
          logError(
            'Error refreshing prices after update',
            {
              component: 'PricingModal',
              action: 'refreshPricesAfterUpdate',
              metadata: { productId, error },
            },
            { showToast: false, userFacing: false }
          );
        }
      };
      refreshPricesAfterUpdate();

      // Exit edit mode
      setEditingPrice(null);
      setActiveTab('table');

      // Call the onPricesUpdated callback
      if (onPricesUpdated) {
        await onPricesUpdated();
      }
    } catch (error) {
      logError(
        'Error updating price',
        {
          component: 'PricingModal',
          action: 'handleUpdatePrice',
          metadata: { productId, editingPrice, newPrice, error },
        },
        { showToast: false, userFacing: false }
      );
      toast.error(getPricingModalNotification('updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Start editing a price
  const handleEditPrice = (price: ProductPrice, isDraft: boolean = false) => {
    setEditingPrice(price);
    setIsEditingDraft(isDraft);
    setActiveTab('add'); // Reuse the add tab for editing
  };

  // Delete a price
  const handleDeletePrice = async (priceId: number, isDraft: boolean = false) => {
    if (!confirm('Are you sure you want to delete this price?')) return;

    // Handle draft price deletion
    if (isDraft) {
      updateDraftPrices(prevDrafts => prevDrafts.filter(draft => draft.id !== priceId));
      toast.success(getPricingModalNotification('priceDeleted'));
      return;
    }

    // Handle real price deletion
    if (!productId) {
      toast.error('Cannot delete price');
      return;
    }

    setLoading(true);
    try {
      await productService.deletePrice(productId, priceId);
      toast.success(getPricingModalNotification('priceDeleted'));

      // Refresh prices
      const refreshPricesAfterDelete = async () => {
        try {
          const pricesData = await productService.getPrices(productId);
          setPrices(pricesData);
        } catch (error) {
          logError(
            'Error refreshing prices after delete',
            {
              component: 'PricingModal',
              action: 'refreshPricesAfterDelete',
              metadata: { productId, error },
            },
            { showToast: false, userFacing: false }
          );
        }
      };
      refreshPricesAfterDelete();

      // Call the onPricesUpdated callback
      if (onPricesUpdated) {
        await onPricesUpdated();
      }
    } catch (error) {
      logError(
        'Error deleting price',
        {
          component: 'PricingModal',
          action: 'handleDeletePrice',
          metadata: { productId, priceId, error },
        },
        { showToast: false, userFacing: false }
      );
      toast.error(getPricingModalNotification('deleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingPrice(null);
    setIsEditingDraft(false);
    setActiveTab('table');
  };

  // Component to display pricing details
  const PricingDetails = ({ prices }: { prices: ProductPrice[] }) => {
    if (!prices || prices.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <p className="text-muted-foreground">{getPricingModalLabels.table.noData}</p>
        </div>
      );
    }

    // Sort prices by creation date (newest first) and then by type
    const sortedPrices = [...prices].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateB - dateA; // Newest first
    });

    return (
      <div className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{getPricingModalLabels.table.headers.priceType}</TableHead>
              <TableHead>{getPricingModalLabels.table.headers.amount}</TableHead>
              <TableHead>{getProductComponentsText('pricingModal.validPeriod')}</TableHead>
              <TableHead className="w-20 text-right">
                {getPricingModalLabels.table.headers.actions}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedPrices.map(price => (
              <TableRow key={price.id}>
                <TableCell className="font-medium">{price.price_type_display}</TableCell>
                <TableCell>{formatCurrencyWithOrgFormat(price.amount, price.currency)}</TableCell>
                <TableCell>
                  {formatDateWithOrgFormat(price.valid_from) ? (
                    <span>
                      {formatDateWithOrgFormat(price.valid_from)}
                      {price.valid_to && ` - ${formatDateWithOrgFormat(price.valid_to)}`}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Not specified</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditPrice(price)}
                      title={getProductComponentsText('pricingModal.tooltips.editPrice')}
                    >
                      <PencilIcon className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeletePrice(price.id)}
                      title={getProductComponentsText('pricingModal.tooltips.deletePrice')}
                    >
                      <TrashIcon className="size-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  // Helper function to format currency with organization preferences
  const formatCurrencyWithOrgFormat = (
    amount: number | string | null | undefined,
    currency: string
  ) => {
    // Convert to number and handle edge cases
    let numericAmount: number;

    if (typeof amount === 'number') {
      numericAmount = amount;
    } else if (typeof amount === 'string') {
      numericAmount = parseFloat(amount);
    } else {
      numericAmount = 0;
    }

    // Handle invalid numbers
    if (isNaN(numericAmount)) {
      numericAmount = 0;
    }

    if (numberFormat === 'comma') {
      const formattedNumber = formatNumberForDisplay(numericAmount, 'comma', 2);
      return `${formattedNumber} ${currency}`;
    } else {
      return formatCurrency(numericAmount, currency);
    }
  };

  // Helper function to format dates with organization format
  const formatDateWithOrgFormat = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return formatDateForDisplay(date, dateFormat);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {getPricingModalLabels.title}
            {product && (
              <span className="ml-1 font-normal text-muted-foreground">{product.name}</span>
            )}
          </DialogTitle>
          <DialogDescription>{getPricingModalLabels.description}</DialogDescription>
        </DialogHeader>

        {/* Show loading state if we're still fetching data */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="mb-2 size-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading product data...</p>
          </div>
        ) : (
          <>
            <Tabs
              defaultValue="table"
              value={activeTab}
              onValueChange={value => setActiveTab(value as any)}
            >
              <TabsList className="mb-4 grid w-full grid-cols-2">
                <TabsTrigger value="table">{getPricingModalLabels.tabs.current}</TabsTrigger>
                <TabsTrigger value="add">
                  {editingPrice
                    ? getPricingModalLabels.form.buttons.updatePrice
                    : getPricingModalLabels.form.buttons.addPrice}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="table" className="space-y-4">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-medium">
                    {prices.length} {prices.length === 1 ? 'Price' : 'Prices'}
                  </h3>
                  <Button onClick={() => setActiveTab('add')} disabled={loading}>
                    {getPricingModalLabels.form.buttons.addPrice}
                  </Button>
                </div>

                {loading ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="mb-2 size-8 animate-spin text-primary" />
                    <p className="text-muted-foreground">
                      {getPricingModalLabels.table.loadingPrices}
                    </p>
                  </div>
                ) : (
                  <PricingDetails prices={productId ? prices : actualDraftPrices} />
                )}
              </TabsContent>

              <TabsContent value="add">
                {/* Form for adding or editing prices */}
                <AddPriceForm
                  newPrice={newPrice}
                  onChange={setNewPrice}
                  onAdd={handleAddPrice}
                  onUpdate={handleUpdatePrice}
                  editing={!!editingPrice}
                  loading={loading}
                  priceTypes={priceTypes}
                  currencies={currencies}
                  onCancel={handleCancelEdit}
                />
              </TabsContent>
            </Tabs>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {getPricingModalLabels.buttons.close}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface AddPriceFormProps {
  newPrice: NewPrice;
  onChange: (np: NewPrice) => void;
  onAdd: () => void;
  onUpdate: () => void;
  editing: boolean;
  loading: boolean;
  priceTypes: any[];
  currencies: any[];
  onCancel: () => void;
}

export function AddPriceForm({
  newPrice,
  onChange,
  onAdd,
  onUpdate,
  editing,
  loading,
  priceTypes,
  currencies,
  onCancel,
}: AddPriceFormProps) {
  // Get organization settings to show defaults and formatting preferences
  const { defaultCurrency, defaultPriceType, orgSettings } = useOrgSettings();
  const numberFormat = orgSettings?.number_format || 'dot';

  // Get translations
  const { getPricingModalLabels } = useProductDetailPageTranslations();

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    onChange({ ...newPrice, [name]: value });
  };

  // Handle amount input with number formatting
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    // Format the input according to organization preferences
    const formattedValue = formatNumberInput(value, numberFormat);

    onChange({ ...newPrice, amount: formattedValue });
  };

  // Handle price type selection
  const handlePriceTypeChange = (value: string) => {
    onChange({ ...newPrice, price_type_id: parseInt(value, 10) });
  };

  // Handle currency selection
  const handleCurrencyChange = (value: string) => {
    onChange({ ...newPrice, currency: value });
  };

  return (
    <div className="space-y-4 p-2">
      {/* Price Type */}
      <div className="space-y-2">
        <Label htmlFor="priceType">{getPricingModalLabels.form.labels.priceType} *</Label>
        <Select
          value={newPrice.price_type_id.toString()}
          onValueChange={handlePriceTypeChange}
          disabled={loading}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                defaultPriceType
                  ? `Default: ${defaultPriceType.label}`
                  : getPricingModalLabels.form.placeholders.selectPriceType
              }
            />
          </SelectTrigger>
          <SelectContent>
            {priceTypes
              .filter(pt => pt.id && pt.id.toString() !== '') // Filter out empty IDs
              .map(pt => (
                <SelectItem key={pt.id} value={pt.id.toString()}>
                  {pt.label}
                  {defaultPriceType && pt.id === defaultPriceType.id && (
                    <span className="ml-2 text-xs text-muted-foreground">(Default)</span>
                  )}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {/* Currency */}
      <div className="space-y-2">
        <Label htmlFor="currency">{getPricingModalLabels.form.labels.currency} *</Label>
        <Select value={newPrice.currency} onValueChange={handleCurrencyChange} disabled={loading}>
          <SelectTrigger>
            <SelectValue
              placeholder={
                defaultCurrency
                  ? `Default: ${defaultCurrency.iso_code}`
                  : getPricingModalLabels.form.placeholders.selectCurrency
              }
            />
          </SelectTrigger>
          <SelectContent>
            {currencies
              .filter(c => c.iso_code && c.iso_code !== '') // Filter out empty iso_code values
              .map(c => (
                <SelectItem key={c.iso_code} value={c.iso_code}>
                  {c.iso_code} ({c.symbol})
                  {defaultCurrency && c.iso_code === defaultCurrency.iso_code && (
                    <span className="ml-2 text-xs text-muted-foreground">(Default)</span>
                  )}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {/* Amount */}
      <div className="space-y-2">
        <Label htmlFor="amount">{getPricingModalLabels.form.labels.amount} *</Label>
        <Input
          name="amount"
          value={newPrice.amount}
          onChange={handleAmountChange}
          placeholder={getPricingModalLabels.form.placeholders.enterAmount}
          disabled={loading}
        />
      </div>

      {/* Valid From */}
      <div className="space-y-2">
        <Label htmlFor="valid_from">{getPricingModalLabels.form.labels.validFrom}</Label>
        <Input
          name="valid_from"
          type="date"
          value={newPrice.valid_from || ''}
          onChange={handleChange}
          disabled={loading}
        />
      </div>

      {/* Valid To */}
      <div className="space-y-2">
        <Label htmlFor="valid_to">{getPricingModalLabels.form.labels.validTo}</Label>
        <Input
          name="valid_to"
          type="date"
          value={newPrice.valid_to || ''}
          onChange={handleChange}
          disabled={loading}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-2 pt-4">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          {getPricingModalLabels.form.buttons.cancel}
        </Button>
        <Button onClick={editing ? onUpdate : onAdd} disabled={loading}>
          {loading && <Loader2 className="mr-2 size-4 animate-spin" />}
          {editing
            ? getPricingModalLabels.form.buttons.updatePrice
            : getPricingModalLabels.form.buttons.addPrice}
        </Button>
      </div>
    </div>
  );
}
